//LooksSearchResultsPage.js

import React, { useState, useEffect, useContext } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import LooksPost from './LooksPost';
import { LooksPostsContext } from '../contexts/LooksPostsContext';
import { useAuth } from '../contexts/AuthContext';
import VisibilityTracker from './VisibilityTracker';
import './HomePage.css';                 // re-use the same grid helpers

const LooksSearchResultsPage = () => {
  /* ───── hooks & context ───── */
  const [searchParams]  = useSearchParams();
  const searchQuery     = searchParams.get('query') || '';
  const { posts, deletePostById } = useContext(LooksPostsContext);
  const { currentUser } = useAuth();
  const navigate        = useNavigate();

  /* ───── local state ───── */
  const [results,  setResults]  = useState([]);
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState(null);

  /* ───── update <title> tag ───── */
  useEffect(() => {
    document.title = searchQuery
      ? `Search results for '${searchQuery}'`
      : 'Search results';
  }, [searchQuery]);

  /* ───── filter whenever query OR posts change ───── */
  useEffect(() => {
    if (!searchQuery) {
      setResults([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const q = searchQuery.toLowerCase().trim();
      const filtered = posts.filter(
        (p) =>
          p.title.toLowerCase().includes(q) ||
          p.author.toLowerCase().includes(q) ||
          (p.description && p.description.toLowerCase().includes(q))
      );
      setResults(filtered);
    } catch (err) {
      console.error(err);
      setError('Error filtering search results');
    } finally {
      setLoading(false);
    }
  }, [searchQuery, posts]);

  /* ───── optional delete hook (author view) ───── */
  const handleDelete = async (id) => {
    if (!window.confirm('Delete this post?')) return;
    try {
      await deletePostById(id);          // context helper
      setResults((curr) => curr.filter((p) => p.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const navigateToCategoryPosts = (category) => {
    navigate(`/categoryposts/${category}`);
    window.scrollTo(0, 0);
  };

  /* ───── render ───── */
  if (loading) return <div>Loading…</div>;
  if (error)   return <div>Error: {error}</div>;

  return (
    <div className="posts">
      <h2 className="search-results-heading" style={{ textAlign: 'center', marginBottom: 30 }}>
        {searchQuery ? `Search results for “${searchQuery}”` : 'Search results'}
      </h2>

      {results.length === 0 ? (
        <p style={{ textAlign: 'center' }}>
          No results found for “{searchQuery}”
        </p>
      ) : (
        results.map((post) => (
          <VisibilityTracker
            key={post.id}
            postId={post.id}
            isDiscoveryBox={false}
            isPostAuthor={post.author === currentUser?.displayName}
          >
            <div className="post-container">
              <LooksPost
                {...post}
                navigateToCategoryPosts={navigateToCategoryPosts}
                onDelete={() => handleDelete(post.id)}
              />
            </div>
          </VisibilityTracker>
        ))
      )}
    </div>
  );
};

export default LooksSearchResultsPage;
